import React, { useState } from 'react';
import { PenLine } from 'lucide-react';
import type { Bed, MapMode, Zone } from '../../types';
import { Button } from '../ui/Button';
import { Input, Select, Textarea } from '../ui/Input';

const SUN_OPTIONS = [
  { value: 'full_sun',   label: 'Full Sun' },
  { value: 'part_shade', label: 'Part Shade' },
  { value: 'full_shade', label: 'Full Shade' },
  { value: 'varies',     label: 'Varies' },
];

interface BedFormProps {
  initial?: Partial<Bed>;
  zones: Zone[];
  mapMode: MapMode;
  drawingPoints: [number, number][];
  onSetMapMode: (m: MapMode) => void;
  onClearDrawing: () => void;
  onSubmit: (data: Partial<Bed>) => Promise<void>;
  onCancel: () => void;
  isEdit?: boolean;
}

export function BedForm({
  initial,
  zones,
  mapMode,
  drawingPoints,
  onSetMapMode,
  onClearDrawing,
  onSubmit,
  onCancel,
  isEdit,
}: BedFormProps) {
  const [form, setForm] = useState<Partial<Bed>>({
    name: '',
    zone_id: '',
    description: '',
    sun_exposure: undefined,
    soil_notes: '',
    irrigation_notes: '',
    ...initial,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (key: keyof Bed, value: unknown) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name?.trim()) { setError('Name is required'); return; }
    setSaving(true);

    const polygonJson =
      drawingPoints.length >= 3
        ? JSON.stringify(drawingPoints)
        : (isEdit ? initial?.polygon_json : undefined);

    try {
      await onSubmit({
        ...form,
        zone_id: form.zone_id || null,
        description: form.description || null,
        sun_exposure: (form.sun_exposure as Bed['sun_exposure']) || null,
        soil_notes: form.soil_notes || null,
        irrigation_notes: form.irrigation_notes || null,
        polygon_json: polygonJson ?? initial?.polygon_json ?? null,
      });
    } catch (err) {
      setError((err as Error).message);
      setSaving(false);
    }
  };

  const isDrawing = mapMode === 'draw-bed';
  const hasNewPolygon = drawingPoints.length >= 3;
  const hasExistingPolygon = !!initial?.polygon_json;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {error && <p role="alert" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}

      <Input
        label="Bed Name *"
        value={form.name ?? ''}
        onChange={(e) => set('name', e.target.value)}
        placeholder="e.g. Vegetable Garden"
        required
      />

      <Select
        label="Zone"
        value={form.zone_id ?? ''}
        onChange={(e) => set('zone_id', e.target.value)}
      >
        <option value="">No zone</option>
        {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
      </Select>

      <Textarea
        label="Description"
        value={form.description ?? ''}
        onChange={(e) => set('description', e.target.value)}
        placeholder="What grows here, special notes…"
        rows={2}
      />

      <Select
        label="Sun Exposure"
        value={form.sun_exposure ?? ''}
        onChange={(e) => set('sun_exposure', e.target.value)}
      >
        <option value="">Unknown</option>
        {SUN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </Select>

      <Textarea
        label="Soil Notes"
        value={form.soil_notes ?? ''}
        onChange={(e) => set('soil_notes', e.target.value)}
        placeholder="Amended, sandy, clay…"
        rows={2}
      />

      <Textarea
        label="Irrigation Notes"
        value={form.irrigation_notes ?? ''}
        onChange={(e) => set('irrigation_notes', e.target.value)}
        placeholder="Drip, overhead, hand water…"
        rows={2}
      />

      {/* Polygon drawing */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-800">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Map Shape</p>
        {hasNewPolygon ? (
          <div className="flex items-center gap-2">
            <PenLine size={16} className="text-garden-600" />
            <span className="text-sm text-gray-600 dark:text-gray-300">{drawingPoints.length} points drawn</span>
            <Button type="button" variant="ghost" size="sm" onClick={onClearDrawing}>Clear</Button>
          </div>
        ) : hasExistingPolygon && !isDrawing ? (
          <div className="flex items-center gap-2">
            <PenLine size={16} className="text-garden-600" />
            <span className="text-sm text-gray-600">Existing polygon</span>
            <Button type="button" variant="ghost" size="sm" onClick={() => onSetMapMode('draw-bed')}>
              Redraw
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant={isDrawing ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => onSetMapMode(isDrawing ? 'view' : 'draw-bed')}
          >
            <PenLine size={14} />
            {isDrawing
              ? `Drawing… (${drawingPoints.length} pts — click map, finish below)`
              : 'Draw polygon on map'}
          </Button>
        )}
        {isDrawing && drawingPoints.length >= 3 && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="mt-2"
            onClick={() => onSetMapMode('view')}
          >
            Finish drawing
          </Button>
        )}
      </div>

      <div className="flex gap-2 pt-1">
        <Button type="submit" loading={saving} className="flex-1">
          {isEdit ? 'Save Changes' : 'Add Bed'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}
